import { cloneElement, useId } from "react";

import cx from "@hsl/core/utils/cx";

import { useTabsContext } from "../context";
import type { TabTypes } from "../Tabs";

export interface Tab {
    key: string;
    label: React.ReactNode;
    href?: string;
    icon?: React.ReactElement;
}

export interface TabProps {
    tab: Tab;
    className?: string;
    current?: boolean;
    iconClassName?: string;
    type?: TabTypes;
    onClick?(): void;
    disabled?: boolean;
}

const Tab = ({
    tab,
    className,
    current,
    iconClassName,
    type = "default",
    onClick,
    disabled,
}: TabProps) => {
    const id = useId();
    const { label, href, icon } = tab;
    const toggleSwitching = useTabsContext()?.toggleSwitching;
    const handleClick: React.MouseEventHandler<
        HTMLButtonElement | HTMLAnchorElement
    > = (e) => {
        if (disabled) return;
        e.stopPropagation();
        toggleSwitching && toggleSwitching();
        !!onClick && onClick();
    };

    switch (type) {
        case "default":
            return (
                <a
                    key={id}
                    href={href}
                    className={cx(
                        current
                            ? "border-b-core-blue text-core-blue border-t-transparent"
                            : "text-charcoal hover:border-b-warm-gray border-y-transparent",
                        "group mr-6 inline-flex flex-row items-center border-y-[3px] py-2 text-sm font-medium transition-colors",
                        !disabled && "cursor-pointer",
                        className,
                    )}
                    aria-current={current ? "page" : undefined}
                    onClick={handleClick}
                >
                    <TabIcon icon={icon} iconClassName={iconClassName} />
                    <span className="max-sm:mt-1 max-sm:text-center">
                        {label}
                    </span>
                </a>
            );
        case "pill":
            return (
                <button
                    key={id}
                    className={cx(
                        current
                            ? "bg-blue-500 text-white"
                            : "bg-platinum hover:bg-warm-gray",
                        "mr-2 rounded-full px-3 py-1 text-sm font-medium transition-colors",
                        !disabled && "cursor-pointer",
                        className,
                    )}
                    onClick={handleClick}
                >
                    {label}
                </button>
            );
        case "text":
            return (
                <a
                    key={id}
                    href={href}
                    className={cx(
                        current
                            ? "text-charcoal font-bold"
                            : " hover:text-charcoal text-taupe-grey",
                        "mr-4 text-sm font-medium transition-colors",
                        !disabled && "cursor-pointer",
                        className,
                    )}
                    aria-current={current ? "page" : undefined}
                    onClick={handleClick}
                >
                    <TabIcon icon={icon} iconClassName={iconClassName} />
                    {label}
                </a>
            );
        default:
            console.warn("Invalid tab type - recieved: ", type);
            return null;
    }
};

export default Tab;

const TabIcon = ({
    icon,
    iconClassName,
}: Pick<Tab, "icon"> & Pick<TabProps, "iconClassName">) => {
    return (
        <>
            {!!icon && (
                <span
                    className={cx(
                        "flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-current p-1 transition-colors sm:-ml-0.5 sm:mr-2",
                        iconClassName,
                    )}
                >
                    {cloneElement(icon)}
                </span>
            )}
        </>
    );
};
